import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface Video {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl: string;
  isIntro: boolean;
  requiredSubscription: boolean;
  module: number;
  category?: string;
  difficulty?: string;
  duration?: number;
  moduleOrder?: number;
  schede?: string;
}

interface NextVideoSuggestionProps {
  open: boolean;
  onClose: () => void;
  onPlayNext: (videoId: string) => void;
  onPlayPrev: (videoId: string) => void;
  nextVideo: Video | null;
  prevVideo?: Video | null;
  loading?: boolean;
}

const NextVideoSuggestion: React.FC<NextVideoSuggestionProps> = ({
  open,
  onClose,
  onPlayNext,
  onPlayPrev,
  nextVideo,
  prevVideo = null,
  loading = false
}) => {
  const { t } = useTranslation();

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handlePlayNext = () => {
    if (nextVideo) {
      onPlayNext(nextVideo.id);
      onClose();
    }
  };
  const handlePlayPrev = () => {
    if (prevVideo) {
      onPlayPrev(prevVideo.id);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(145deg, rgba(45, 45, 45, 0.95) 0%, rgba(60, 60, 60, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(227, 28, 37, 0.3)',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            zIndex: 1,
            background: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.7)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header */}
        <Box sx={{
          p: 3,
          textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(227, 28, 37, 0.1) 0%, transparent 100%)',
          borderBottom: '1px solid rgba(227, 28, 37, 0.2)'
        }}>
          <CheckCircleIcon sx={{ 
            color: '#4CAF50', 
            fontSize: 48, 
            mb: 2 
          }} />
          <Typography variant="h5" sx={{ 
            color: 'white', 
            fontWeight: 700,
            mb: 1,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
          }}>
            {t('videoPlayer.videoCompleted')}
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            mb: 2
          }}>
            {t('videoPlayer.suggestNextVideo')}
          </Typography>
        </Box>

        {/* Previous and Next Video Cards */}
        <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: prevVideo && nextVideo ? '1fr 1fr' : '1fr' }, gap: 2 }}>
          {prevVideo && (
            <Card sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(227, 28, 37, 0.2)',
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.08)',
                borderColor: 'rgba(227, 28, 37, 0.4)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(227, 28, 37, 0.2)'
              }
            }}>
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <CardMedia component="img" sx={{ width: 200, height: 120, objectFit: 'cover' }} image={prevVideo.thumbnailUrl} alt={prevVideo.title} />
                </Box>
                <CardContent sx={{ flex: 1, p: 2 }}>
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>{t('common.previous')}</Typography>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1, lineHeight: 1.3 }}>{prevVideo.title}</Typography>
                  <Button variant="outlined" onClick={handlePlayPrev} startIcon={<PlayArrowIcon />} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.1)' }}}>
                    {t('videoPlayer.playPrevious', 'Play previous')}
                  </Button>
                </CardContent>
              </Box>
            </Card>
          )}
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 200 
            }}>
              <CircularProgress sx={{ color: '#E31C25' }} />
            </Box>
          ) : nextVideo ? (
            <Card sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(227, 28, 37, 0.2)',
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.08)',
                borderColor: 'rgba(227, 28, 37, 0.4)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(227, 28, 37, 0.2)'
              }
            }}>
              <Box sx={{ display: 'flex' }}>
                {/* Thumbnail */}
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <CardMedia
                    component="img"
                    sx={{
                      width: 200,
                      height: 120,
                      objectFit: 'cover'
                    }}
                    image={nextVideo.thumbnailUrl}
                    alt={nextVideo.title}
                  />
                  {/* Play Overlay */}
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(227, 28, 37, 0.9)',
                    borderRadius: '50%',
                    width: 50,
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.8,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 1,
                      transform: 'translate(-50%, -50%) scale(1.1)'
                    }
                  }}>
                    <PlayArrowIcon sx={{ color: 'white', fontSize: 30 }} />
                  </Box>
                  {/* Duration */}
                  {nextVideo.duration && (
                    <Box sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      background: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem'
                    }}>
                      {formatDuration(nextVideo.duration)}
                    </Box>
                  )}
                </Box>

                {/* Video Info */}
                <CardContent sx={{ flex: 1, p: 2 }}>
                  <Typography variant="h6" sx={{ 
                    color: 'white',
                    fontWeight: 700,
                    mb: 1,
                    lineHeight: 1.3
                  }}>
                    {nextVideo.title}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    mb: 2,
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {nextVideo.description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                      label={t('dashboard.moduleLabel', { number: nextVideo.module })}
                      size="small"
                      sx={{
                        background: '#E31C25',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                    {nextVideo.difficulty && (
                      <Chip
                        label={nextVideo.difficulty}
                        size="small"
                        sx={{
                          bgcolor: (() => {
                            const d = nextVideo.difficulty!.toLowerCase();
                            if (d === 'beginner') return 'rgba(76, 175, 80, 0.2)';
                            if (d === 'intermediate') return 'rgba(255, 193, 7, 0.2)';
                            if (d === 'advanced' || d === 'hard') return 'rgba(244, 67, 54, 0.2)';
                            return 'rgba(255, 255, 255, 0.2)';
                          })(),
                          color: (() => {
                            const d = nextVideo.difficulty!.toLowerCase();
                            if (d === 'beginner') return '#4CAF50';
                            if (d === 'intermediate') return '#FFC107';
                            if (d === 'advanced' || d === 'hard') return '#F44336';
                            return '#FFFFFF';
                          })(),
                          border: (() => {
                            const d = nextVideo.difficulty!.toLowerCase();
                            if (d === 'beginner') return '1px solid rgba(76, 175, 80, 0.35)';
                            if (d === 'intermediate') return '1px solid rgba(255, 193, 7, 0.35)';
                            if (d === 'advanced' || d === 'hard') return '1px solid rgba(244, 67, 54, 0.35)';
                            return '1px solid rgba(255, 255, 255, 0.2)';
                          })()
                        }}
                      />
                    )}
                    {nextVideo.moduleOrder && (
                      <Chip
                        label={`#${nextVideo.moduleOrder}`}
                        size="small"
                        sx={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white'
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Box>
            </Card>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              color: 'rgba(255, 255, 255, 0.6)'
            }}>
              <Typography variant="body1">
                {t('videoPlayer.noNextVideo')}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              background: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          {t('common.close')}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
        {prevVideo && (
          <Button onClick={handlePlayPrev} variant="text" startIcon={<PlayArrowIcon />} sx={{ color: 'white' }}>
            {t('videoPlayer.playPrevious', 'Play previous')}
          </Button>
        )}
        {nextVideo && (
          <Button
            onClick={handlePlayNext}
            variant="contained"
            startIcon={<PlayArrowIcon />}
            sx={{
              background: 'linear-gradient(135deg, #E31C25 0%, #ff4757 100%)',
              color: 'white',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #ff4757 0%, #E31C25 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(227, 28, 37, 0.4)'
              }
            }}
          >
            {t('videoPlayer.playNext')}
          </Button>
        )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default NextVideoSuggestion;

